import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Clock, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';

export default function ReferralBanner() {
  const { user } = useAuthStore();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string | null>(null);
  const [hasShared, setHasShared] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  // Sprawdź czy jest aktywna zniżka przy montowaniu komponentu
  useEffect(() => {
    if (!user) return;

    const discountTime = localStorage.getItem(`referralDiscount_${user.id}`);
    if (discountTime) {
      const expiryTime = parseInt(discountTime);
      if (expiryTime > Date.now()) {
        setVerified(true);
        startTimer(expiryTime);
      } else {
        localStorage.removeItem(`referralDiscount_${user.id}`);
      }
    }
  }, [user]);

  const startTimer = (expiryTime: number) => {
    const updateTimer = () => {
      const now = Date.now();
      const timeLeft = expiryTime - now;
      
      if (timeLeft <= 0) {
        setVerified(false);
        setTimeLeft(null);
        localStorage.removeItem(`referralDiscount_${user?.id}`);
        return;
      }

      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft(`${hours}h ${minutes}m`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  };

  const handleShare = async () => {
    if (!user) {
      toast.error('Musisz być zalogowany');
      return;
    }

    const shareData = {
      title: 'Kariera z AI - Twój Doradca Kariery',
      text: 'Odkryj swoją idealną ścieżkę kariery z pomocą sztucznej inteligencji!',
      url: `${window.location.origin}?ref=${user.id}`
    };

    try {
      if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        await navigator.share(shareData);
        setHasShared(true);
        toast.success('Udostępniono pomyślnie! Kliknij Zweryfikuj aby aktywować zniżkę na 24h');
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setShowCopiedToast(true);
        toast.success('Link skopiowany! Kliknij Zweryfikuj aby aktywować zniżkę na 24h');
        setHasShared(true);
        setTimeout(() => setShowCopiedToast(false), 2000);
      }
    } catch (err) {
      console.error('Share error:', err);
      toast.error('Nie udało się udostępnić');
      setHasShared(false);
    }
  };

  const handleVerify = async () => {
    if (!user || !hasShared) {
      toast.error('Najpierw udostępnij link znajomym!');
      return;
    }
    
    setIsVerifying(true);
    try {
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem(`referralDiscount_${user.id}`, expiryTime.toString());
      
      setVerified(true);
      startTimer(expiryTime);
      
      toast.success('Gratulacje! Otrzymujesz 20% zniżki na plan Premium', {
        duration: 5000,
        icon: '🎉'
      });

      // Odśwież stronę aby zastosować zniżkę
      window.location.reload();
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Wystąpił błąd podczas weryfikacji');
    } finally {
      setIsVerifying(false);
    }
  };

  if (!user) return null;

  return (
    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl p-6 shadow-lg">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
          <Users className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-xl font-bold mb-2">
            Poleć znajomym i zyskaj 20% zniżki!
          </h3>
          <p className="text-white/90">
            Udostępnij Kariera z AI znajomym i odbierz 20% zniżki na plan Premium
          </p>
        </div>
      </div>

      {verified ? (
        <div className="flex items-center gap-2 px-4 py-2 bg-green-500/90 backdrop-blur-sm text-white rounded-lg">
          <span>Zniżka 20% aktywna! {timeLeft && `Pozostało: ${timeLeft}`}</span>
          <ArrowRight className="w-4 h-4" />
        </div>
      ) : (
        <div className="flex flex-wrap gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleShare}
            className="px-6 py-2 bg-white text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors"
          >
            {showCopiedToast ? 'Skopiowano!' : 'Udostępnij i zyskaj 20% zniżki'}
          </motion.button>

          {hasShared && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleVerify}
              disabled={isVerifying}
              className="px-6 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-400 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isVerifying ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Weryfikuję...
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4" />
                  Zweryfikuj udostępnienia
                </>
              )}
            </motion.button>
          )}
        </div>
      )}
    </div>
  );
}
